import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useTenant } from '@/tenant/TenantProvider';
import { ProMark, useScrollFade } from '@/ui';

const LINKS = [
  { to: '/reviews', key: 'overview', end: true },
  { to: '/reviews/requests', key: 'requests' },
  { to: '/reviews/feedback', key: 'feedback' },
  { to: '/reviews/settings', key: 'settings' },
  // Google Business Profile: only when the platform switch is on (ADR-G7).
  { to: '/reviews/google', key: 'google', pro: true, platformFeature: 'google.business' },
] as const;

/** Section tabs shared by the review pages (rendered under the PageHeader rule). */
export function ReviewsSectionNav({ badges }: { badges?: Partial<Record<string, number>> }) {
  const { t } = useTranslation('reviews');
  const { hasFeature, isOnPlatform } = useTenant();
  const fade = useScrollFade<HTMLElement>({ revealActive: true });
  return (
    <nav
      ref={fade}
      aria-label={t('section')}
      className="scroll-fade -mb-px mt-4 flex gap-6 overflow-x-auto"
    >
      {LINKS.filter((l) => !('platformFeature' in l) || isOnPlatform(l.platformFeature)).map(
        (l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={'end' in l ? l.end : false}
            className={({ isActive }) =>
              cn(
                'group inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 pb-2.5 pt-1 text-[0.875rem] font-semibold transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                isActive
                  ? 'active border-primary text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink',
              )
            }
          >
            {t(`nav.${l.key}`)}
            {'pro' in l && l.pro && !hasFeature('google.business') && <ProMark />}
            {badges?.[l.key] ? (
              // Same count bubble as TabsTrigger: one rule for every tab bar.
              <span className="num rounded-full bg-sunken px-1.5 text-2xs font-semibold text-ink-muted group-[.active]:bg-primary group-[.active]:text-on-primary">
                {badges[l.key]}
              </span>
            ) : null}
          </NavLink>
        ),
      )}
    </nav>
  );
}
